"use client"

import { useEffect, useRef } from "react"
import { ScanLine } from "lucide-react"

import { cn } from "@/lib/utils"
import { trackEvent, runWhenPageVisible } from "@/lib/analytics"

export interface HeroSocialProofProps {
  countLabel?: string | null
  className?: string
  /** Identifier for the rendered social-proof treatment. Used to attribute
   * impressions/conversions when running A/B tests via PostHog feature flags.
   * Bump the suffix (e.g. "count_v1" → "count_v2", "testimonial_v1") whenever
   * the visual or copy treatment changes so historical funnels stay clean. */
  variant?: string
}

export function HeroSocialProof({
  countLabel,
  className,
  variant = "count_v1",
}: HeroSocialProofProps) {
  // One impression per mount: a late count refresh or variant swap re-runs
  // the effect but must not emit a second impression for the same pageview.
  const firedRef = useRef(false)

  useEffect(() => {
    if (!countLabel || firedRef.current) return
    // Visibility-gated: background-tab opens and SERP prerenders mount this
    // component while hidden; an impression only counts once someone sees it.
    return runWhenPageVisible(() => {
      if (firedRef.current) return
      firedRef.current = true
      trackEvent("hero_social_proof_viewed", {
        variant,
        count_label: countLabel,
      })
    })
  }, [variant, countLabel])

  if (!countLabel) return null

  return (
    <div
      className={cn(
        "flex flex-col items-center gap-1 mb-6",
        className,
      )}
      data-testid="hero-social-proof"
      data-variant={variant}
    >
      <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
        <ScanLine className="h-4 w-4 text-green-600" aria-hidden="true" />
        <span className="font-bold">{countLabel}</span>
      </div>
    </div>
  )
}
